import { useEffect, useState, useCallback } from 'react';
import { mgmt, KcGroup, KcUser } from '../services/api';
import { errMsg } from '../utils';

export default function GroupsPage() {
  const [roots, setRoots] = useState<KcGroup[]>([]);
  const [childrenOf, setChildrenOf] = useState<Record<string, KcGroup[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<KcGroup | null>(null);
  const [err, setErr] = useState('');

  const loadRoots = useCallback(async () => {
    setErr('');
    try {
      const r = await mgmt.listGroups();
      setRoots(r.data.groups);
    } catch (e) {
      setErr(errMsg(e));
    }
  }, []);

  useEffect(() => {
    loadRoots();
  }, [loadRoots]);

  const toggle = async (g: KcGroup) => {
    const next = new Set(expanded);
    if (next.has(g.id)) {
      next.delete(g.id);
    } else {
      next.add(g.id);
      if (!childrenOf[g.id]) {
        try {
          const r = await mgmt.getGroupChildren(g.id);
          setChildrenOf((c) => ({ ...c, [g.id]: r.data.children }));
        } catch (e) {
          setErr(errMsg(e));
        }
      }
    }
    setExpanded(next);
  };

  const createGroup = async (parentId?: string) => {
    const name = window.prompt(parentId ? 'Name der Untergruppe' : 'Name der Gruppe');
    if (!name) return;
    try {
      await mgmt.createGroup({ name, parentId });
      if (parentId) {
        const r = await mgmt.getGroupChildren(parentId);
        setChildrenOf((c) => ({ ...c, [parentId]: r.data.children }));
      } else {
        loadRoots();
      }
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const removeGroup = async (g: KcGroup) => {
    if (!window.confirm(`Gruppe "${g.path}" löschen?`)) return;
    try {
      await mgmt.deleteGroup(g.id);
      if (selected?.id === g.id) setSelected(null);
      loadRoots();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const renderNode = (g: KcGroup, depth: number) => (
    <div key={g.id}>
      <div
        className="flex items-center gap-1 rounded px-1 py-0.5 text-sm hover:bg-panel"
        style={{ paddingLeft: depth * 14 }}
      >
        <button className="w-4 text-gray-400" onClick={() => toggle(g)}>
          {expanded.has(g.id) ? '▾' : '▸'}
        </button>
        <button
          className={`flex-1 text-left ${selected?.id === g.id ? 'text-coral' : 'text-gray-200'}`}
          onClick={() => setSelected(g)}
        >
          {g.name}
        </button>
        <button className="text-xs text-gray-500 hover:text-gray-300" onClick={() => createGroup(g.id)}>
          + Sub
        </button>
        <button className="text-xs text-coral/70 hover:text-coral" onClick={() => removeGroup(g)}>
          ✕
        </button>
      </div>
      {expanded.has(g.id) && (childrenOf[g.id] ?? []).map((c) => renderNode(c, depth + 1))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Gruppen</h2>
        <button className="btn-primary" onClick={() => createGroup()}>
          Gruppe anlegen
        </button>
      </div>
      {err && <p className="text-coral">{err}</p>}

      <div className="grid grid-cols-2 gap-6">
        <section className="card">
          <h3 className="mb-2 font-medium text-coral">Gruppenbaum</h3>
          {roots.length === 0 && <p className="text-gray-500">Keine Gruppen.</p>}
          {roots.map((g) => renderNode(g, 0))}
        </section>

        <section className="card">
          {selected ? (
            <GroupMembers group={selected} />
          ) : (
            <p className="text-gray-500">Gruppe auswählen, um Mitglieder zu verwalten.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function GroupMembers({ group }: { group: KcGroup }) {
  const [members, setMembers] = useState<KcUser[]>([]);
  const [addId, setAddId] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await mgmt.getGroupMembers(group.id);
      setMembers(r.data.members);
    } catch (e) {
      setErr(errMsg(e));
    }
  }, [group.id]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!addId.trim()) return;
    try {
      await mgmt.addGroupMember(group.id, addId.trim());
      setAddId('');
      load();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const remove = async (userId: string) => {
    try {
      await mgmt.removeGroupMember(group.id, userId);
      load();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="font-medium text-coral">Mitglieder · {group.path}</h3>
      <div className="flex gap-2">
        <input
          className="input"
          placeholder="Keycloak User-ID"
          value={addId}
          onChange={(e) => setAddId(e.target.value)}
        />
        <button className="btn-primary" disabled={!addId} onClick={add}>
          Hinzufügen
        </button>
      </div>
      {err && <p className="text-coral">{err}</p>}
      <ul className="text-sm">
        {members.map((m) => (
          <li key={m.id} className="flex items-center justify-between border-t border-edge py-1.5">
            <span className="text-gray-200">
              {m.username}
              {m.email && <span className="text-gray-500"> · {m.email}</span>}
            </span>
            <button className="text-xs text-coral/70 hover:text-coral" onClick={() => remove(m.id)}>
              Entfernen
            </button>
          </li>
        ))}
        {members.length === 0 && <li className="py-2 text-gray-500">Keine Mitglieder.</li>}
      </ul>
    </div>
  );
}
